import { cn } from "@moe/priv/utils";

interface Props {
  searchText?: string;
  tags?: string[];
  onClear?: () => void;
  className?: string;
}

/**
 * Renders a search description component that displays the current search filters and provides a button to clear all filters.
 *
 * @param {Props} props - The component props.
 * @param {string} [props.searchText] - The current search text.
 * @param {string[]} [props.tags] - The current search tags.
 * @param {() => void} [props.onClear] - A callback function to clear all search filters.
 */
export function SearchDescription({ searchText, tags, onClear, className }: Props) {
  const hasFilters = !!searchText || !!tags;
  if (!hasFilters) return null;
  return (
    <div className={cn("flex flex-col", className)}>
      <span className="text-tx-tertiary font-medium">{getFilterDescription({ searchText, tags })}</span>
      {/* Show clear button if we have any search filters active */}
      {(searchText || !!tags?.length) && (
        <button
          className="text-tx-tertiary hover:text-tx-primary w-fit text-sm font-semibold underline"
          onClick={() => {
            onClear?.();
          }}
        >
          Clear All
        </button>
      )}
    </div>
  );
}

export const getFilterDescription = ({ searchText, tags }: { searchText?: string; tags?: string[] }): string => {
  const parts = [];
  if (searchText) {
    parts.push(`matching "${searchText}"`);
  }
  if (tags?.length) {
    const tagCount = tags.length;
    if (!searchText) {
      return tagCount === 1 ? `tagged with "${tags[0]}"` : `matching ${tagCount} tags`;
    }
    parts.push(`filtered by ${tagCount} ${tagCount === 1 ? "tag" : "tags"}`);
  }
  return parts.join(" ");
};
